import { Box, Flex, Input, IconButton, useDisclosure } from '@chakra-ui/react';
import { FormEvent, useRef, useState, useEffect } from 'react';
import { HiOutlinePaperClip, HiPaperAirplane } from 'react-icons/hi2';
import { uploadFile } from 'src/services/fileUpload';
import useImage from 'src/hooks/useImage';
import DragAndDropContainer from './DragAndDropContainer';
import ImagePreviewList from './ImagePreview';
import ZoomedImage from './ZoomedImage';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

const Actions = () => {
	const [content, setContent] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [isSendingMessage, setIsSendingMessage] = useState(false);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const {
		sendMessage,
		shouldShowTypingIndicator,
		isLoading: isChatLoading,
		messages,
	} = useRealtimeChatContext();

	const {
		selectedImages,
		uploadErrors,
		addImages,
		removeImage,
		setUploadErrors,
		removeAllImages,
	} = useImage();

	const focusInput = () => {
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus();
			}
		}, 100);
	};

	useEffect(() => {
		focusInput();
	}, [messages.length]);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if ((!content && !selectedImages.length) || shouldShowTypingIndicator)
			return;

		setIsSendingMessage(true);
		setUploadErrors({});

		let imagesMarkdownText = '';
		const newUploadErrors: Record<string, string> = {};

		if (selectedImages.length) {
			const uploadPromises = selectedImages.map(async (file) => {
				try {
					const { imageUrl } = await uploadFile(file, false, 100, 100, 100, 400, false, false, false);
					return `![](${imageUrl})`;
				} catch (err: any) {
					newUploadErrors[file.name] =
						err.response?.data?.message || 'Upload failed.';
					return null;
				}
			});

			const results = await Promise.all(uploadPromises);
			imagesMarkdownText = results.filter(Boolean).join(' ');

			if (Object.keys(newUploadErrors).length > 0) {
				setUploadErrors(newUploadErrors);
				setIsSendingMessage(false);
				return;
			}
		}

		const updatedContent =
			content + (imagesMarkdownText ? ` ${imagesMarkdownText}` : '');

		sendMessage(updatedContent);

		setContent('');
		removeAllImages();
		setIsSendingMessage(false);
		focusInput();
	};

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			const files = Array.from(e.target.files);
			addImages(files);
		}
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleImageClick = (src: string) => {
		setSelectedImage(src);
		onOpen();
	};

	return (
		<>
			<Box
				bg="gray.100"
				py="24px"
				px="16px"
				pt={selectedImages.length ? '12px' : '24px'}
				minH="80px"
				width="100%"
				h="auto"
				boxShadow={{ base: '0 -2px 8px rgba(0, 0, 0, 0.1)', md: 'none' }}
			>
				{selectedImages.length > 0 && (
					<ImagePreviewList
						images={selectedImages}
						errors={uploadErrors}
						onRemove={removeImage}
						onClick={handleImageClick}
					/>
				)}

				<DragAndDropContainer onDropFiles={addImages}>
					<form onSubmit={handleSubmit} style={{ width: '100%' }}>
						<Box
							borderWidth="1px"
							borderRadius="md"
							display="flex"
							alignItems="center"
							bg="white"
							pl={3}
						>
							<Input
								ref={inputRef}
								placeholder="Tell us..."
								variant="unstyled"
								flex="1"
								_focus={{ outline: 'none' }}
								h="40px"
								fontSize={{ base: '16px', md: '14px' }}
								minW="70%"
								value={content}
								onChange={(e) => setContent(e.target.value)}
							/>

							<Flex gap={0}>
								<IconButton
									aria-label="Attach"
									icon={
										<HiOutlinePaperClip
											style={{ width: '20px', height: '20px' }}
										/>
									}
									variant="blank"
									onClick={() =>
										document.getElementById('image-upload-multiple')?.click()
									}
								/>
								<Input
									id="image-upload-multiple"
									type="file"
									accept="image/*"
									multiple
									display="none"
									onChange={handleFileUpload}
									ref={fileInputRef}
								/>

								<IconButton
									aria-label="Send"
									icon={
										<HiPaperAirplane
											style={{ width: '20px', height: '20px' }}
										/>
									}
									isDisabled={isChatLoading || shouldShowTypingIndicator}
									isLoading={isSendingMessage}
									type="submit"
									variant="blank"
									color="orange.base"
								/>
							</Flex>
						</Box>
					</form>
				</DragAndDropContainer>
			</Box>

			{selectedImage && (
				<ZoomedImage
					isOpen={isOpen}
					onClose={onClose}
					imageSrc={selectedImage}
				/>
			)}
		</>
	);
};

export default Actions;
